import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  User, Mail, Phone, MapPin, Shield, Star, Loader2, Save, ShieldCheck, ShieldX,
  CheckCircle2, Trash2, AlertTriangle, Camera, Briefcase, Clock, ArrowLeft
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import AccountActions from '../../components/Profile/AccountActions';
import ChangePasswordCard from '../../components/Profile/ChangePasswordCard';
import LegalAndPrivacyCard from '../../components/Profile/LegalAndPrivacyCard';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WorkerProfileEdit() {
  const { user, api, refreshUser, logout, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // Walkthrough anchor — only emit on mobile (desktop uses sidebar nav link)
  const isLargeScreen = useIsLargeScreen();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', preferredArea: '',
    preferredRole: '', experienceLevel: '', experienceType: '', confidenceNote: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        fullName: user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
        preferredArea: user.preferredArea || '',
        preferredRole: user.preferredRole || '',
        experienceLevel: user.experienceLevel || '',
        experienceType: user.experienceType || '',
        confidenceNote: user.confidenceNote || '',
      });
    }
  }, [user]);

  const fetchRatings = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.get(`/ratings/worker/${user.id}`);
      setRatings(res.data);
    } catch {}
  }, [api, user?.id]);

  useEffect(() => { fetchRatings(); }, [fetchRatings]);

  const handleSave = async () => {
    if (!form.fullName.trim()) { toast.error('Name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    setSaving(true);
    try {
      await api.patch('/users/me', form);
      await refreshUser();
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/users/me/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
      toast.success('Profile photo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${API_URL}/api/files/${path}?auth=${token}`;
  };

  if (!user) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="worker-profile-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-full h-11 w-11" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-worker-profile' : undefined}>Profile</h1>
          <p className="text-slate-500 mt-1">Manage your profile and preferences</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start gap-5 mb-6">
            {/* Profile Photo with Upload */}
            <div className="relative group">
              {user.profilePhoto ? (
                <img
                  src={getPhotoUrl(user.profilePhoto)}
                  alt={user.fullName}
                  className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-md"
                  data-testid="profile-photo"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-md" data-testid="profile-avatar">
                  {user.fullName?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                aria-label="Upload profile photo"
                className="absolute -bottom-1 -right-1 h-11 w-11 rounded-full bg-[#0f1b3d] text-white flex items-center justify-center shadow-lg hover:bg-[#1a2d5a] transition-colors border-2 border-white"
                data-testid="upload-photo-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handlePhotoUpload} data-testid="photo-file-input" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-slate-900">{user.fullName}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {user.preferredRole && (
                  <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                    <Briefcase className="h-3 w-3" /> {user.preferredRole}
                  </Badge>
                )}
                {user.experienceLevel && (
                  <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {user.experienceLevel}
                  </Badge>
                )}
                {user.smartIdVerified ? (
                  <Badge className="bg-emerald-100 text-emerald-800 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Smart ID</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1"><ShieldX className="h-3 w-3" /> Unverified</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-center gap-1 text-amber-500 mb-1">
                <Star className="h-4 w-4 fill-current" />
                <span className="text-xl font-bold text-slate-900">{user.ratingAverage?.toFixed(1) || '0.0'}</span>
              </div>
              <p className="text-xs text-slate-500">Rating</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-xl">
              <div className="text-xl font-bold text-slate-900 mb-1">{user.completedShiftsCount || 0}</div>
              <p className="text-xs text-slate-500">Shifts</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-xl">
              <div className="text-xl font-bold text-slate-900 mb-1">{user.reliabilityScore?.toFixed(0) || '100'}%</div>
              <p className="text-xs text-slate-500">Reliable</p>
              <p
                className="text-[10.5px] text-slate-400 mt-1 leading-snug"
                data-testid="reliability-label"
              >
                Based on your shift completion history. Visible to managers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4"><CardTitle className="text-lg">Edit Profile</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><User className="h-4 w-4" /> Full Name</Label>
            <Input value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><Mail className="h-4 w-4" /> Email</Label>
            <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-email-input" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><Phone className="h-4 w-4" /> Phone</Label>
            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-phone-input" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4" /> Preferred Area</Label>
            <Input value={form.preferredArea} onChange={e => setForm(p => ({ ...p, preferredArea: e.target.value }))} className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-area-input" />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><Briefcase className="h-4 w-4" /> Role</Label>
            <Select value={form.preferredRole} onValueChange={v => setForm(p => ({ ...p, preferredRole: v }))}>
              <SelectTrigger className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-role-select">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Counter Assistant">Counter Assistant</SelectItem>
                <SelectItem value="Supervisor">Supervisor</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="Trainee">Trainee</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Experience */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><Clock className="h-4 w-4" /> Counter Experience</Label>
            <Select value={form.experienceLevel} onValueChange={v => setForm(p => ({ ...p, experienceLevel: v }))}>
              <SelectTrigger className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-experience-select">
                <SelectValue placeholder="Select experience" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Less than 3 months">Less than 3 months</SelectItem>
                <SelectItem value="3–6 months">3–6 months</SelectItem>
                <SelectItem value="6–12 months">6–12 months</SelectItem>
                <SelectItem value="1–2 years">1–2 years</SelectItem>
                <SelectItem value="2+ years">2+ years</SelectItem>
                {/* Legacy values still supported for backward compat */}
                <SelectItem value="0-1 years">0-1 years (legacy)</SelectItem>
                <SelectItem value="2-5 years">2-5 years (legacy)</SelectItem>
                <SelectItem value="5+ years">5+ years (legacy)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Experience Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-700"><Briefcase className="h-4 w-4" /> Experience Type</Label>
            <Select value={form.experienceType} onValueChange={v => setForm(p => ({ ...p, experienceType: v }))}>
              <SelectTrigger className="h-12 rounded-xl bg-slate-50 border-slate-200" data-testid="profile-experience-type-select">
                <SelectValue placeholder="Where is your experience from?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Post Office">Post Office</SelectItem>
                <SelectItem value="Retail">Retail</SelectItem>
                <SelectItem value="Customer service">Customer service</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Confidence Note */}
          <div className="space-y-2">
            <Label className="text-slate-700">Tell managers what you’re confident doing <span className="text-slate-400 font-normal">(optional)</span></Label>
            <textarea
              value={form.confidenceNote}
              onChange={e => setForm(p => ({ ...p, confidenceNote: e.target.value.slice(0, 280) }))}
              rows={3}
              placeholder="e.g., Comfortable with PO counter, parcels, banking, MoneyGram."
              className="w-full rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0f1b3d]/20 resize-none"
              data-testid="profile-confidence-note"
            />
            <p className="text-xs text-slate-400 text-right">{(form.confidenceNote || '').length}/280</p>
          </div>

          <Button className="w-full h-12 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white font-medium" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Ratings */}
      {ratings.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4"><CardTitle className="text-lg">Recent Ratings</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {ratings.slice(0, 5).map(r => (
              <div key={r.id} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-1 mb-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`h-4 w-4 ${i <= r.ratingValue ? 'text-amber-400 fill-current' : 'text-slate-300'}`} />
                  ))}
                  <span className="text-xs text-slate-400 ml-2">{new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                </div>
                {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Account Actions: Replay Walkthrough + Delete Account (soft delete) */}
      <ChangePasswordCard />
      <LegalAndPrivacyCard />
      <AccountActions />

      {/* Phase 6 UX: link to unified Settings so profile does not duplicate
          notification / privacy / logout controls. */}
      <div className="bg-white rounded-2xl shadow-sm p-5 flex items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-slate-900">Settings</p>
          <p className="text-sm text-slate-500">Notifications, privacy, security, help and log-out.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/worker/settings')}
          className="rounded-full min-h-[44px] px-4 border-2 border-slate-200 text-slate-800 hover:bg-slate-50 text-sm font-medium"
          data-testid="profile-settings-link"
        >
          Open settings
        </button>
      </div>
    </div>
  );
}
